import React, { useCallback, useRef, useState } from "react";
import { useShow } from "@mksap/components/hooks/useShow";
import { useUniqueId } from "@mksap/components/hooks/useUniqueId";
import { Tooltip } from "../ui/MksapReactBootstrap";
import { Button } from "./Button";
import { InlineOverlay } from "./InlineOverlay";

// This is a tooltip that displays on click and will hide on click or scroll.
// We had a similar version in 18. Hiding on scroll prevented the tooltip from
// appearing over top of the toolbar. This tooltip defaults to position top but
// will reset the position to bottom if the target is too close to the top app
// bar or modal window.
interface ClickableTooltipProps {
	content: React.ReactNode;
	children: React.ReactNode;
	onShow?(): void;
	onHide?(): void;
	className?: string;
	tooltipClassName?: string;
	ariaLabel?: string;
}

export const ClickableTooltip: React.FC<ClickableTooltipProps> = ({
	content,
	children,
	onShow,
	onHide,
	className,
	tooltipClassName,
	ariaLabel,
}) => {
	const { showing, show, hide } = useShow();
	const target = useRef<HTMLAnchorElement | null>(null);
	const uniqueId = useUniqueId();
	const scrollableEl = useRef<HTMLElement | null>(null);
	const handleScroll = useCallback(() => hide(), [hide]);

	const [placement, setPlacement] = useState<"top" | "bottom">("top");

	const handleClick = useCallback(() => {
		if (showing) {
			hide();
			return;
		}

		// Calculate the custom placement based on the target position at onclick
		if (target.current) {
			const targetPositions = target.current.getBoundingClientRect();
			if (targetPositions.top < 170) {
				setPlacement("bottom");
			} else {
				setPlacement("top");
			}
		}

		show();
	}, [target, show, hide, showing]);

	const handleEntered = useCallback(() => {
		if (!target.current) {
			return;
		}

		// Assume we are in a related content modal
		scrollableEl.current = target.current.closest(".modal-body");

		// If we aren't in a related content modal, set the scrollable el to <main>
		if (!scrollableEl.current) {
			scrollableEl.current = document.querySelector("main");
		}

		// Add handler to scroll event which hides the tooltip on scroll
		if (scrollableEl.current) {
			scrollableEl.current.addEventListener("scroll", handleScroll);
		}
	}, [scrollableEl, handleScroll, target]);

	const handleExit = useCallback(() => {
		if (onHide) {
			onHide();
		}

		// Remove handler responsible for hiding tooltip on scroll
		if (scrollableEl.current) {
			scrollableEl.current.removeEventListener("scroll", handleScroll);
		}
	}, [onHide, scrollableEl, handleScroll]);

	return (
		<>
			<Button
				variant="inline-link"
				onClick={handleClick}
				className={className}
				ref={target}
				aria-expanded={showing}
				aria-describedby={showing ? uniqueId : undefined}
				aria-label={ariaLabel}
			>
				{children}
			</Button>
			<InlineOverlay
				show={showing}
				target={() => target.current}
				rootClose={true}
				onEnter={onShow}
				onHide={hide}
				onEntered={handleEntered}
				onExit={handleExit}
				placement={placement}
			>
				{(props) => (
					<Tooltip id={uniqueId} className={tooltipClassName} {...props}>
						{content}
					</Tooltip>
				)}
			</InlineOverlay>
		</>
	);
};
